import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { type TopicIdentityRecord } from "@/shared/domain/topics";
import { type PaginatedResponse } from "@/shared/http";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseSessionTopicsParams {
  sessionId: string;
  pageToken?: string;
}

/**
 * Fetches one page of topic identities contributing to a Session.
 *
 * Returns the raw `PaginatedResponse` so callers can drive a paginator
 * off `next_token`. The shell mounts only the first page.
 */
export function useSessionTopics({
  sessionId,
  pageToken,
}: UseSessionTopicsParams): UseQueryResult<
  PaginatedResponse<TopicIdentityRecord>,
  Error
> {
  const { sessionService } = useDomainServices();
  const orgId = useCurrentOrgId();
  return useQuery<PaginatedResponse<TopicIdentityRecord>, Error>({
    queryKey: sessionQueryKeys.topics(sessionId, pageToken),
    queryFn: ({ signal }) =>
      sessionService.listSessionTopics(sessionId, {
        abortSignal: signal,
        pageToken,
        resourceOwnerId: orgId,
      }),
  });
}
